/**
 * useOpenAsTopic — Hook for creating a topic from an existing entity.
 *
 * Used by MeetingConversationPage, SessionsPage, ThreadDetail, and
 * ChatTabContent to create a topic and open it in the topic bar.
 */

import { useState, useCallback } from 'react';
import { useTopicBar } from '../contexts/TopicBarContext';
import { topicsApi, type Topic, type WorkpieceType } from '../api/topics';

interface OpenAsTopicInput {
  type?: string;
  title: string;
  description?: string;
  object_type?: string;
  object_id?: string;
  workpiece_type?: WorkpieceType;
  workpiece_id?: string | null;
  assigned_agents?: string[];
  /** Item to attach after topic creation */
  item?: {
    item_type: string;
    item_id: string;
    label?: string;
  };
}

const ITEM_WORKPIECE_TYPES: Record<string, Exclude<WorkpieceType, 'none'>> = {
  email_thread: 'email',
  document: 'document',
  board: 'issue_list',
  issue_list: 'issue_list',
  spreadsheet: 'spreadsheet',
  contact: 'contact',
};

function inferWorkpiece(input: OpenAsTopicInput): { workpiece_type?: WorkpieceType; workpiece_id?: string | null } {
  if (input.workpiece_type) {
    return {
      workpiece_type: input.workpiece_type,
      workpiece_id: input.workpiece_type === 'none'
        ? null
        : input.workpiece_id ?? input.object_id ?? input.item?.item_id ?? null,
    };
  }

  const itemType = input.item?.item_type ?? input.object_type;
  const workpieceType = itemType ? ITEM_WORKPIECE_TYPES[itemType] : undefined;
  if (!workpieceType) return {};

  return {
    workpiece_type: workpieceType,
    workpiece_id: input.item?.item_id ?? input.object_id ?? null,
  };
}

function shouldEnsureWorkpiece(topic: Topic, inferred: { workpiece_type?: WorkpieceType; workpiece_id?: string | null }): boolean {
  if (!inferred.workpiece_type || inferred.workpiece_type === 'none' || !inferred.workpiece_id) return false;
  return topic.workpiece_type === 'none'
    || (topic.workpiece_type === inferred.workpiece_type && !topic.workpiece_id);
}

export function useOpenAsTopic() {
  const { createTopic, addTopic, focusTopic, updateTopic, allTopics } = useTopicBar();
  const [isCreating, setIsCreating] = useState(false);

  /** Find existing topic for this object, or create + open a new one */
  const openAsTopic = useCallback(async (input: OpenAsTopicInput) => {
    const inferredWorkpiece = inferWorkpiece(input);

    // Check if a topic already exists for this object
    if (input.object_type && input.object_id) {
      const existing = allTopics.find(
        t => t.object_type === input.object_type && t.object_id === input.object_id
      );
      if (existing) {
        addTopic(existing);
        focusTopic(existing.id);
        if (input.item && shouldEnsureWorkpiece(existing, inferredWorkpiece)) {
          await topicsApi.addItem(existing.id, {
            item_type: input.item.item_type,
            item_id: input.item.item_id,
            label: input.item.label,
          }).catch(console.error);
          const refreshed = await topicsApi.get(existing.id).catch(() => null);
          if (refreshed?.topic) {
            updateTopic(existing.id, refreshed.topic);
            return refreshed.topic;
          }
        }
        return existing;
      }
    }

    setIsCreating(true);
    try {
      const topic = await createTopic({
        type: input.type,
        title: input.title,
        description: input.description,
        object_type: input.object_type,
        object_id: input.object_id,
        ...inferredWorkpiece,
        assigned_agents: input.assigned_agents,
      });

      if (topic && input.item) {
        // addItem is fire-and-forget — topic is already open
        await topicsApi.addItem(topic.id, {
          item_type: input.item.item_type,
          item_id: input.item.item_id,
          label: input.item.label,
        }).catch(console.error);
        const refreshed = await topicsApi.get(topic.id).catch(() => null);
        if (refreshed?.topic) {
          updateTopic(topic.id, refreshed.topic);
          return refreshed.topic;
        }
      }

      return topic;
    } finally {
      setIsCreating(false);
    }
  }, [createTopic, addTopic, focusTopic, updateTopic, allTopics]);

  return { openAsTopic, isCreating };
}
